import { useState, useMemo } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { 
  Trash2, 
  GraduationCap, 
  Globe2, 
  Award,
  BookOpen,
  CheckCircle2,
  Clock,
  AlertCircle
} from 'lucide-react'
import { 
  Qualification, 
  QualificationType,
  NQFLevel,
  InternationalEquivalency,
  CESMCategory,
  QUALIFICATION_LABELS, 
  QUALIFICATION_NQF_MAP,
  QUALIFICATION_NQF_RANGE,
  getValidNQFLevels,
  PROFESSIONAL_BODIES,
  SA_UNIVERSITIES,
  CESM_CATEGORIES
} from '@/types/onboarding'

interface QualificationFormProps {
  qualification: Qualification
  index: number
  onChange: (qualification: Qualification) => void
  onRemove: () => void
  disabled?: boolean
  showAdvancedFields?: boolean
}

// NQF Level descriptions for tooltips/badges
const NQF_LEVEL_DESCRIPTIONS: Record<NQFLevel, string> = {
  1: 'Grade 9 / ABET Level 4',
  2: 'Grade 10 / NC(V) Level 2',
  3: 'Grade 11 / NC(V) Level 3',
  4: 'Grade 12 / NSC / NC(V) Level 4',
  5: 'Higher Certificate (120+ credits)',
  6: 'Diploma / Advanced Cert (240-360 credits)',
  7: "Bachelor's / B-Tech / Adv Diploma (360-480 credits)",
  8: 'Honours / PG Diploma (120+ credits)',
  9: "Master's Degree (180+ credits)",
  10: 'Doctoral Degree / PhD (360+ credits)',
}

// International equivalency labels
const EQUIVALENCY_LABELS: Record<InternationalEquivalency, string> = {
  nqf4_equivalent: 'NQF 4 Equivalent (High School)',
  nqf5_equivalent: 'NQF 5 Equivalent (Certificate)',
  nqf6_equivalent: 'NQF 6 Equivalent (Diploma)',
  nqf7_equivalent: "NQF 7 Equivalent (Bachelor's)",
  nqf8_equivalent: 'NQF 8 Equivalent (Honours)',
  nqf9_equivalent: "NQF 9 Equivalent (Master's)",
  nqf10_equivalent: 'NQF 10 Equivalent (PhD)',
  pending_saqa: 'Awaiting SAQA Evaluation',
  unknown: 'Unknown Equivalency',
}

export default function QualificationForm({
  qualification,
  index,
  onChange,
  onRemove,
  disabled = false,
  showAdvancedFields = false
}: QualificationFormProps) {
  const [institutionSearch, setInstitutionSearch] = useState('')
  const [showInstitutionSuggestions, setShowInstitutionSuggestions] = useState(false)

  // Get NQF level - use stored value for variable types, or fixed mapping
  const nqfLevel = useMemo(() => {
    if (qualification.type === 'professional' || qualification.type === 'international') {
      return qualification.nqfLevel
    }
    // For types with variable levels, use stored value if set
    if (QUALIFICATION_NQF_RANGE[qualification.type]) {
      return qualification.nqfLevel || QUALIFICATION_NQF_RANGE[qualification.type]?.default
    }
    return QUALIFICATION_NQF_MAP[qualification.type as keyof typeof QUALIFICATION_NQF_MAP]
  }, [qualification.type, qualification.nqfLevel])

  // Check if this qualification type has variable NQF levels
  const hasVariableNqfLevel = useMemo(() => {
    return !!QUALIFICATION_NQF_RANGE[qualification.type]
  }, [qualification.type])

  // Get valid NQF levels for current type
  const validNqfLevels = useMemo(() => {
    return getValidNQFLevels(qualification.type)
  }, [qualification.type])

  // Filter SA universities for autocomplete
  const filteredInstitutions = useMemo(() => {
    if (!institutionSearch || qualification.country === 'international') return []
    const search = institutionSearch.toLowerCase()
    return SA_UNIVERSITIES.filter(uni => 
      uni.toLowerCase().includes(search)
    ).slice(0, 15)
  }, [institutionSearch, qualification.country])

  // Update handler
  const handleUpdate = (updates: Partial<Qualification>) => {
    onChange({ ...qualification, ...updates })
  }

  // Handle qualification type change
  const handleTypeChange = (type: QualificationType) => {
    const updates: Partial<Qualification> = { type }
    
    // Auto-populate NQF level for SA qualifications
    if (type !== 'professional' && type !== 'international') {
      // Use range default for variable types, otherwise fixed mapping
      const range = QUALIFICATION_NQF_RANGE[type]
      updates.nqfLevel = range 
        ? range.default 
        : QUALIFICATION_NQF_MAP[type as keyof typeof QUALIFICATION_NQF_MAP]
      updates.country = 'south_africa'
    } else if (type === 'international') {
      updates.country = 'international'
      updates.nqfLevel = undefined
    }
    
    // Reset conditional fields
    if (type !== 'professional') {
      updates.professionalBody = undefined
      updates.membershipNumber = undefined
      updates.membershipStatus = undefined
    }
    if (type !== 'international') {
      updates.internationalEquivalency = undefined
      updates.saqaEvaluated = undefined
      updates.saqaReferenceNumber = undefined
    }
    if (type !== 'honours' && type !== 'masters' && type !== 'phd') {
      updates.thesis = undefined
      updates.thesisTitle = undefined
    }
    
    handleUpdate(updates)
  }

  // Determine qualification category for conditional field display
  const isProfessionalCert = qualification.type === 'professional'
  const isAcademicQualification = !isProfessionalCert
  const isNSC = qualification.type === 'nsc'
  const isPostgraduate = ['pgdip', 'honours', 'masters', 'phd'].includes(qualification.type)
  const showThesisFields = ['honours', 'masters', 'phd'].includes(qualification.type)

  return (
    <Card className={`bg-muted/30 ${disabled ? 'opacity-60' : ''}`}>
      <CardContent className="p-4 space-y-4">
        {/* Header with NQF Badge */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-sm">Qualification #{index + 1}</h4>
            {nqfLevel && (
              <Badge 
                variant="outline" 
                className="bg-heritage-blue/10 text-heritage-blue border-heritage-blue/30"
                title={NQF_LEVEL_DESCRIPTIONS[nqfLevel]}
              >
                NQF {nqfLevel}
              </Badge>
            )}
            {qualification.inProgress && (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                <Clock className="h-3 w-3 mr-1" />
                In Progress
              </Badge>
            )}
            {qualification.verified && (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Verified
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!disabled && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onRemove}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Row 1: Qualification Type & Country */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">
              Qualification Type <span className="text-red-500">*</span>
            </Label>
            <Select
              value={qualification.type}
              onValueChange={(value) => handleTypeChange(value as QualificationType)}
              disabled={disabled}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nsc">
                  <div className="flex items-center gap-2">
                    <GraduationCap className="h-3 w-3" />
                    {QUALIFICATION_LABELS.nsc}
                  </div>
                </SelectItem>
                <SelectItem value="higher_cert">{QUALIFICATION_LABELS.higher_cert}</SelectItem>
                <SelectItem value="diploma">{QUALIFICATION_LABELS.diploma}</SelectItem>
                <SelectItem value="adv_cert">{QUALIFICATION_LABELS.adv_cert}</SelectItem>
                <SelectItem value="adv_diploma">{QUALIFICATION_LABELS.adv_diploma}</SelectItem>
                <SelectItem value="bachelors">{QUALIFICATION_LABELS.bachelors}</SelectItem>
                <SelectItem value="btech">{QUALIFICATION_LABELS.btech}</SelectItem>
                <SelectItem value="pgdip">{QUALIFICATION_LABELS.pgdip}</SelectItem>
                <SelectItem value="honours">{QUALIFICATION_LABELS.honours}</SelectItem>
                <SelectItem value="masters">{QUALIFICATION_LABELS.masters}</SelectItem>
                <SelectItem value="phd">{QUALIFICATION_LABELS.phd}</SelectItem>
                <SelectItem value="professional">
                  <div className="flex items-center gap-2">
                    <Award className="h-3 w-3" />
                    {QUALIFICATION_LABELS.professional}
                  </div>
                </SelectItem>
                <SelectItem value="international">
                  <div className="flex items-center gap-2">
                    <Globe2 className="h-3 w-3" />
                    {QUALIFICATION_LABELS.international}
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Country</Label>
            <Select
              value={qualification.country || 'south_africa'}
              onValueChange={(value) => handleUpdate({ country: value as 'south_africa' | 'international' })}
              disabled={disabled || qualification.type === 'international'}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="south_africa">🇿🇦 South Africa</SelectItem>
                <SelectItem value="international">🌍 International</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* NQF Level Selector - Only for types with variable levels (e.g., Bachelor's can be NQF 7 or 8) */}
          {hasVariableNqfLevel && (
            <div>
              <Label className="text-xs">
                NQF Level <span className="text-amber-600 text-[10px]">(3-year vs 4-year)</span>
              </Label>
              <Select
                value={String(qualification.nqfLevel || QUALIFICATION_NQF_RANGE[qualification.type]?.default)}
                onValueChange={(value) => handleUpdate({ nqfLevel: Number(value) as NQFLevel })}
                disabled={disabled}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {validNqfLevels.map(level => (
                    <SelectItem key={level} value={String(level)}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">NQF {level}</span>
                        <span className="text-xs text-muted-foreground">
                          {level === 7 ? '(3-year / 360 credits)' : '(4-year / 480 credits)'}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">
                4-year professional degrees (LLB, BArch, BEng) are NQF 8
              </p>
            </div>
          )}
        </div>

        {/* Academic Fields - Hidden for Professional Certifications */}
        {isAcademicQualification && (
          <>
            {/* Row 2: Institution with Autocomplete */}
            <div className="relative">
              <Label className="text-xs">
                Institution <span className="text-red-500">*</span>
              </Label>
              <Input
                value={qualification.institution}
                onChange={(e) => {
                  handleUpdate({ institution: e.target.value })
                  setInstitutionSearch(e.target.value)
                  setShowInstitutionSuggestions(true)
                }}
                onFocus={() => setShowInstitutionSuggestions(true)}
                onBlur={() => setTimeout(() => setShowInstitutionSuggestions(false), 200)}
                disabled={disabled}
                className="h-9"
                placeholder={qualification.country === 'international' ? 'Enter institution name' : 'Start typing to search SA institutions...'}
              />
              {showInstitutionSuggestions && filteredInstitutions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                  {filteredInstitutions.map((uni, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                      onMouseDown={() => {
                        handleUpdate({ institution: uni })
                        setShowInstitutionSuggestions(false)
                      }}
                    >
                      {uni}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Row 3: Qualification Name — hidden for Grade 12 (NSC) */}
            {!isNSC && (
              <div>
                <Label className="text-xs">
                  Qualification Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={qualification.qualificationName || ''}
                  onChange={(e) => handleUpdate({ qualificationName: e.target.value })}
                  disabled={disabled}
                  className="h-9"
                  placeholder="e.g., Bachelor of Commerce, Master of Education"
                />
              </div>
            )}

            {/* Row 4: CESM Category & Field of Study — hidden for Grade 12 (NSC) */}
            {!isNSC && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">
                  CESM Category
                  <span className="text-muted-foreground ml-1">(Field of Education)</span>
                </Label>
                <Select
                  value={qualification.cesmCategory || ''}
                  onValueChange={(value) => handleUpdate({ cesmCategory: value as CESMCategory })}
                  disabled={disabled}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {CESM_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.code} value={cat.code}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-muted-foreground">{cat.code}</span>
                          <span className="truncate">{cat.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {qualification.cesmCategory && (
                  <p className="text-xs text-muted-foreground mt-1">
                    e.g., {CESM_CATEGORIES.find(c => c.code === qualification.cesmCategory)?.examples.slice(0, 3).join(', ')}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs">Specialization / Major</Label>
                <Input
                  value={qualification.fieldOfStudy || ''}
                  onChange={(e) => handleUpdate({ fieldOfStudy: e.target.value })}
                  disabled={disabled}
                  className="h-9"
                  placeholder="e.g., Accounting, Software Engineering"
                />
              </div>
            </div>
            )}
          </>
        )}

        {/* Year & Status - Different layout for Professional Certs */}
        {isProfessionalCert ? (
          // Professional Certification: Just Year Registered (no "Currently studying")
          <div>
            <Label className="text-xs">Year Registered</Label>
            <Input
              type="number"
              min="1950"
              max="2030"
              value={qualification.yearCompleted || ''}
              onChange={(e) => handleUpdate({ yearCompleted: e.target.value })}
              disabled={disabled}
              className="h-9 max-w-[200px]"
              placeholder="2020"
            />
          </div>
        ) : (
          // Academic Qualifications: Year Completed + Currently Studying option
          <div className={`grid ${qualification.inProgress ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'} gap-3`}>
            <div>
              <Label className="text-xs">Year Completed</Label>
              <Input
                type="number"
                min="1950"
                max="2030"
                value={qualification.yearCompleted || ''}
                onChange={(e) => handleUpdate({ yearCompleted: e.target.value })}
                disabled={disabled || qualification.inProgress}
                className="h-9"
                placeholder="2020"
              />
            </div>
            <div className="flex items-end pb-1">
              <button
                type="button"
                className="flex items-center space-x-2 cursor-pointer"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (!disabled) {
                    const newInProgress = !qualification.inProgress
                    handleUpdate({ 
                      inProgress: newInProgress,
                      yearCompleted: newInProgress ? undefined : qualification.yearCompleted
                    })
                  }
                }}
                disabled={disabled}
              >
                <div className={`h-4 w-4 rounded border flex items-center justify-center ${qualification.inProgress ? 'bg-primary border-primary' : 'border-input'} ${disabled ? 'opacity-50' : ''}`}>
                  {qualification.inProgress && (
                    <CheckCircle2 className="h-3 w-3 text-white" />
                  )}
                </div>
                <span className="text-xs">Currently studying</span>
              </button>
            </div>
            {qualification.inProgress && (
              <div>
                <Label className="text-xs">Expected Completion</Label>
                <Input
                  type="number"
                  min="2024"
                  max="2035"
                  value={qualification.expectedCompletionYear || ''}
                  onChange={(e) => handleUpdate({ expectedCompletionYear: e.target.value })}
                  disabled={disabled}
                  className="h-9"
                  placeholder="2026"
                />
              </div>
            )}
          </div>
        )}

        {/* International Qualification Fields */}
        {qualification.type === 'international' && (
          <div className="border-t pt-3 space-y-3">
            <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 p-2 rounded">
              <Globe2 className="h-4 w-4" />
              <span>International qualifications require SAQA evaluation for NQF equivalency</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">NQF Equivalency</Label>
                <Select
                  value={qualification.internationalEquivalency || 'unknown'}
                  onValueChange={(value) => handleUpdate({ internationalEquivalency: value as InternationalEquivalency })}
                  disabled={disabled}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(EQUIVALENCY_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end pb-1">
                <button
                  type="button"
                  className="flex items-center space-x-2 cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (!disabled) {
                      handleUpdate({ saqaEvaluated: !qualification.saqaEvaluated })
                    }
                  }}
                  disabled={disabled}
                >
                  <div className={`h-4 w-4 rounded border flex items-center justify-center ${qualification.saqaEvaluated ? 'bg-primary border-primary' : 'border-input'} ${disabled ? 'opacity-50' : ''}`}>
                    {qualification.saqaEvaluated && (
                      <CheckCircle2 className="h-3 w-3 text-white" />
                    )}
                  </div>
                  <span className="text-xs">SAQA Evaluated</span>
                </button>
              </div>
            </div>
            {qualification.saqaEvaluated && (
              <div>
                <Label className="text-xs">SAQA Reference Number</Label>
                <Input
                  value={qualification.saqaReferenceNumber || ''}
                  onChange={(e) => handleUpdate({ saqaReferenceNumber: e.target.value })}
                  disabled={disabled}
                  className="h-9"
                  placeholder="e.g., SAQA-2024-12345"
                />
              </div>
            )}
          </div>
        )}

        {/* Professional Certification Fields */}
        {qualification.type === 'professional' && (
          <div className="border-t pt-3 space-y-3">
            <div className="flex items-center gap-2 text-sm text-purple-700 bg-purple-50 p-2 rounded">
              <Award className="h-4 w-4" />
              <span>Professional certifications registered with statutory bodies</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Professional Body <span className="text-red-500">*</span></Label>
                <Select
                  value={qualification.professionalBody || ''}
                  onValueChange={(value) => handleUpdate({ professionalBody: value })}
                  disabled={disabled}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select body" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROFESSIONAL_BODIES.map((body) => (
                      <SelectItem key={body.id} value={body.id}>{body.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Membership Number</Label>
                <Input
                  value={qualification.membershipNumber || ''}
                  onChange={(e) => handleUpdate({ membershipNumber: e.target.value })}
                  disabled={disabled}
                  className="h-9"
                  placeholder="e.g., CA(SA)12345"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Membership Status</Label>
              <Select
                value={qualification.membershipStatus || 'active'}
                onValueChange={(value) => handleUpdate({ membershipStatus: value as 'active' | 'inactive' | 'pending' })}
                disabled={disabled}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-3 w-3 text-green-600" />
                      Active
                    </div>
                  </SelectItem>
                  <SelectItem value="pending">
                    <div className="flex items-center gap-2">
                      <Clock className="h-3 w-3 text-amber-600" />
                      Pending
                    </div>
                  </SelectItem>
                  <SelectItem value="inactive">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-3 w-3 text-gray-600" />
                      Inactive
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Thesis Fields for Masters/PhD */}
        {showThesisFields && (
          <div className="border-t pt-3 space-y-3">
            <div className="flex items-center gap-2 text-sm text-heritage-blue bg-blue-50 p-2 rounded">
              <BookOpen className="h-4 w-4" />
              <span>Research component details</span>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`thesis-${qualification.id}`}
                checked={qualification.thesis || false}
                onCheckedChange={(checked) => handleUpdate({ thesis: !!checked })}
                disabled={disabled}
              />
              <Label htmlFor={`thesis-${qualification.id}`} className="text-xs">
                Includes thesis/dissertation component
              </Label>
            </div>
            {qualification.thesis && (
              <div>
                <Label className="text-xs">Thesis Title</Label>
                <Input
                  value={qualification.thesisTitle || ''}
                  onChange={(e) => handleUpdate({ thesisTitle: e.target.value })}
                  disabled={disabled}
                  className="h-9"
                  placeholder="Enter thesis/dissertation title"
                />
              </div>
            )}
          </div>
        )}

        {/* NQF Level Info Footer - Context-aware for variable level types */}
        {nqfLevel && (
          <div className="text-xs text-muted-foreground bg-gray-50 p-2 rounded flex items-center gap-2">
            <GraduationCap className="h-3 w-3" />
            <span>
              <strong>NQF Level {nqfLevel}:</strong> {
                // Show context-specific description for variable NQF level types
                hasVariableNqfLevel
                  ? nqfLevel === 7
                    ? `3-year ${QUALIFICATION_LABELS[qualification.type]} (360 credits)`
                    : `4-year Professional ${QUALIFICATION_LABELS[qualification.type]} (480 credits)`
                  : NQF_LEVEL_DESCRIPTIONS[nqfLevel]
              }
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
